import {
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  EmojiSadIcon,
} from "@heroicons/react/solid";
import { t } from "i18next";
import moment, { Moment } from "moment-timezone";
import { SlotStates } from "../../api/models/ScheduleSlotModel";
import SessionModel, { SessionStates } from "../../api/models/SessionModel";
import { isValidMoment } from "../../helpers/date_helpers";
import CalendarScheduleSlotModel from "../../models/CalendarScheduleSlotModel";
export const getSessionState = (status: string) => {
  const LocalBasePath = "page.student.dashboard.sessionsList";
  if (status === SessionStates.done || status === SessionStates.completed) {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-green-600 rounded-full ring-green-600 ring-1">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
        <CheckCircleIcon className="w-4" />
      </div>
    );
  } else if (
    status === SessionStates.absence ||
    status === SessionStates.canceled
  ) {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-red-600 rounded-full ring-red-600 ring-1">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
        <EmojiSadIcon className="w-4" />
      </div>
    );
  } else if (
    status === SessionStates.canceled_by_student ||
    status === SessionStates.canceled_by_tutor ||
    status === SessionStates.student_absence ||
    status === SessionStates.tutor_absence
  ) {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-[#DC2626] rounded-full bg-[#FEF2F2] ">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
      </div>
    );
  } else if (status === SessionStates.terminated_early) {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-yellow-500 rounded-full bg-[#FEFCE8] ">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
      </div>
    );
  } else if (
    status === SessionStates.coming ||
    status === SessionStates.in_progress
  ) {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-white rounded-full bg-brandblue-600">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
        <ClockIcon className="w-4" />
      </div>
    );
  } else {
    return (
      <div className="flex items-center justify-between gap-1 p-1 px-2 text-xs font-semibold text-gray-700 bg-gray-200 rounded-full">
        <p className="ltr:ml-1 rtl:mr-1">{t(`${LocalBasePath}.${status}`)}</p>
        <CalendarIcon className="w-4" />
      </div>
    );
  }
};
export const getNoteState = (status: string) => {
  const LocalBasePath = "page.student.dashboard.sessionInfo";

  if (status === "passed") {
    return (
      <span
        data-cy-note-status={status}
        className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
      >
        {t(`${LocalBasePath}.${status}`)}
      </span>
    );
  } else {
    return (
      <span
        data-cy-note-status={status}
        className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
      >
        {t(`${LocalBasePath}.${status}`)}
      </span>
    );
  }
};
export const getCreditState = (amount: number) => {
  const LocalBasePath = "page.tutor.dashboard.credits";
  if (amount >= 0) {
    return (
      <span className="inline-flex items-center text-sm font-semibold text-green-500">
        {`+${amount} $`}
      </span>
    );
  } else if (amount < 0) {
    return (
      <span className="inline-flex items-center text-sm font-semibold text-red-600">
        {` ${amount} $`}
      </span>
    );
  } else {
    return (
      <span className="inline-flex items-center text-sm font-semibold text-gray-500">
        {t(`${LocalBasePath}.donate`)}
      </span>
    );
  }
};
export const sortByDateAndStartTime = (sessions: SessionModel[]) => {
  return sessions.sort((a, b) => {
    const date1 = composeStartTimeAndDate(a.start_time!, a.date!);
    const date2 = composeStartTimeAndDate(b.start_time!, b.date!);

    return moment(date1).diff(date2);
  });
};

export const getSessionsFromScheduleSlots = (
  slots: CalendarScheduleSlotModel[]
) => {
  const _scheduledSessions = slots
    .map((slot) => {
      let refDay = moment();
      if (isValidMoment(slot.date)) {
        refDay = moment(slot.date);
      } else if (slot.dayOfWeek <= moment().day()) {
        refDay = moment().add(1, "week");
      }
      slot.modifyStateByDate(refDay);
      return slot.toSessionModel(refDay);
    })
    .filter((session) => !session.isDone);
  return sortByDateAndStartTime(_scheduledSessions);
};

export const composeStartTimeAndDate = (startTime: Moment, date: Moment) => {
  return date.clone().set({
    hours: startTime.hours(),
    minutes: startTime.minutes(),
    seconds: startTime.seconds(),
  });
};

const canInsert = (session: SessionModel, sessions: SessionModel[]) => {
  // We check if we can insert a session at the end of list of sessions
  // We compare only the session with last inserted session because the session list is already sorted
  if (sessions.length === 0) return true;

  const lastSession = sessions.at(-1);
  const lastStartTime = composeStartTimeAndDate(
    lastSession?.start_time!,
    lastSession?.date!
  );
  const currentStartTime = composeStartTimeAndDate(
    session.start_time!,
    session.date!
  );
  return lastStartTime.isBefore(currentStartTime);
};

export const groupSessions = (
  keyGenerator: (session: SessionModel) => string
) => {
  return (sessions: SessionModel[]) => {
    const groups = sessions.reduce(
      (groups: { [key: string]: SessionModel[] }, session: SessionModel) => {
        const key = keyGenerator(session);
        if (!groups[key]) {
          groups[key] = [];
        }
        if (canInsert(session, groups[key])) groups[key].push(session);
        return groups;
      },
      {}
    );
    return groups;
  };
};

export const groupSessionsByMonth = groupSessions(
  // This format will always return the key of the session as its month and year.
  // We are not interested in the session day here.
  (session: SessionModel) => session.date?.format("MM-YYYY")!
);

export const groupSessionsByStatus = groupSessions(
  (session: SessionModel) => session.statusReported
);

export const renumberGroupedSessions = (
  groupedSessions: {
    [key: string]: SessionModel[];
  },
  sessionsNumber: number,
  reorder: boolean
) => {
  let counter = 0;
  for (let month of Object.values(groupedSessions)) {
    for (let session of month) {
      if (reorder) {
        session.sessionNumber = sessionsNumber + counter;
      } else {
        session.sessionNumber =
          session.sessionNumber ?? sessionsNumber + counter;
      }
      counter++;
    }
  }
  return groupedSessions;
};

export const renumberSessions = (
  sessions: SessionModel[],
  startNumber: number
) => {
  return sessions.map((session, index) => {
    session.sessionNumber = startNumber + index + 1;
    return session;
  });
};

export const openSessionInNewTab = (
  userType: "student" | "tutor" | "candidate"
) => {
  return (sessionId: number | string) => {
    window.open(`/${userType}/dashboard/room/${sessionId}`, "_blank");
  };
};

/**
 *
 * @param sessions =>actual sessions
 * @param scheduledSessions =>scheduled sessions that are generated from schedule slots
 * @returns scheduled sessions after removing the sessions that have been already started
 */
export const removeDuplicatedScheduledSessions = (
  sessions: SessionModel[],
  scheduledSessions: SessionModel[]
) => {
  const sessionsStartTimes = sessions.map((session) =>
    composeStartTimeAndDate(session.start_time!, session.date!).toISOString()
  );

  return scheduledSessions.filter((session) => {
    return !sessionsStartTimes.includes(
      composeStartTimeAndDate(session.start_time!, session.date!).toISOString()
    );
  });
};

export function minutesToMilliseconds(minutes: number) {
  return minutes * 60 * 1000;
}
